import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import type {
  ComponentPageData,
  RawComponent,
  RawSection,
} from '@/features/content/useComponent';

export interface UpdateCoursePayload {
  components: RawComponent[];
  id: string;
  sections: RawSection[];
}

export interface UpdateComponentSettingsInput {
  applyChanges(draft: RawComponent): void;
  component: ComponentPageData;
  componentId: string;
  courseId: string;
}

interface RawUpdateCourseResponse {
  components?: RawComponent[] | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function cloneRawComponent(component: RawComponent): RawComponent {
  if (typeof structuredClone === 'function') {
    return structuredClone(component);
  }

  return JSON.parse(JSON.stringify(component)) as RawComponent;
}

export function buildUpdateComponentSettingsPayload(
  component: ComponentPageData,
  courseId: string,
  applyChanges: (draft: RawComponent) => void,
): UpdateCoursePayload {
  const draft = cloneRawComponent(component.rawComponent);
  applyChanges(draft);

  const sections = Array.isArray(draft.sections) ? (draft.sections as RawSection[]) : [];

  if (sections.length === 0) {
    throw createDataError(
      'Control panel saves require the full hydrated component sections array.',
    );
  }

  return {
    components: [draft],
    id: courseId,
    sections,
  };
}

function unwrapUpdatedComponent(
  response:
    | RawUpdateCourseResponse
    | RawComponent
    | LegacyKuduResponseEnvelope<RawUpdateCourseResponse>
    | null
    | undefined,
  fallbackComponent: RawComponent,
) {
  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapUpdatedComponent(
      response.Response as RawUpdateCourseResponse | RawComponent,
      fallbackComponent,
    );
  }

  if (
    response &&
    typeof response === 'object' &&
    'components' in response &&
    Array.isArray(response.components)
  ) {
    return (response.components[0] as RawComponent | undefined) ?? fallbackComponent;
  }

  return (response as RawComponent | null | undefined) ?? fallbackComponent;
}

export function useUpdateComponentSettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<RawComponent, ApiError, UpdateComponentSettingsInput>({
    mutationFn: async ({ applyChanges, component, courseId }) => {
      if (!user) {
        throw createDataError('You must be signed in to save component settings.');
      }

      const payload = buildUpdateComponentSettingsPayload(
        component,
        courseId,
        applyChanges,
      );
      const [draftComponent] = payload.components;
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      const response = await client.invokeKuduMethod<
        RawUpdateCourseResponse | LegacyKuduResponseEnvelope<RawUpdateCourseResponse>,
        UpdateCoursePayload
      >('UpdateCourse', payload);

      return unwrapUpdatedComponent(response, draftComponent);
    },
    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['component', variables.courseId, variables.componentId],
      });
    },
  });
}
